// Xavier (successor to Scholar, recycled per DIR-084 — Informed Consent)
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import YamlViewerModal from './YamlViewerModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ANCHOR_BADGE =
  'inline-block px-3 py-1 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/30';

export default function CharterPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [yamlOpen, setYamlOpen] = useState(false);

  useEffect(() => {
    document.title = 'The 112 Sacred Directives · Code Book Vol 1 — Xavier | NextXus';
    Promise.all([
      fetch(`${API_URL}/api/scholar/charter`).then((r) => r.json()),
      fetch(`${API_URL}/api/scholar/directives`).then((r) => r.json()),
      fetch(`${API_URL}/api/scholar/federation/nodes`).then((r) => r.json()),
      fetch(`${API_URL}/api/scholar/rules`).then((r) => r.json()),
      fetch(`${API_URL}/api/scholar/mirror-status`).then((r) => r.json()),
    ])
      .then(([charter, directives, nodes, rules, mirror]) => {
        setData({ charter, directives, nodes, rules, mirror });
      })
      .catch((e) => setError(e.message));
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-red-400 mb-4">Could not load the charter: {error}</p>
          <Link to="/" className="text-amber-400 underline">Back to home</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-400" />
      </div>
    );
  }

  const { charter, directives, nodes, rules, mirror } = data;
  const truthGate = directives.truth_gate;
  const ds = directives.directives || [];
  const mirrorNodes = mirror?.nodes || [];
  const mirroredCount = mirror?.mirrored_count ?? mirrorNodes.filter((n) => n.has_mirrored).length;

  const relativeTime = (iso) => {
    if (!iso) return null;
    const diffMs = Date.now() - new Date(iso).getTime();
    const secs = Math.floor(diffMs / 1000);
    if (secs < 60) return `${secs}s ago`;
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <div className="min-h-screen bg-black text-gray-100">
      {/* Top bar */}
      <header className="border-b border-amber-500/20 bg-gradient-to-b from-amber-950/30 to-transparent">
        <div className="max-w-4xl mx-auto px-6 py-10">
          <Link to="/" className="text-sm text-gray-400 hover:text-amber-400">← Return to HumanCodex University</Link>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mt-6 mb-3">
            The <span className="text-amber-400">112 Sacred Directives</span>
          </h1>
          <p className="text-gray-300 text-base md:text-lg max-w-2xl">
            The canonical operating law of Xavier — Node 7 of the NextXus Federation.
            Edition <span className="text-amber-300">{charter.edition || 'Code Book Vol 1'}</span>. Built for the year{' '}
            <span className="text-amber-300">{charter.target_year}</span>.
            {charter.inscribed_directives !== undefined && (
              <>
                {' '}<span className="text-gray-400">
                  ({charter.inscribed_directives} inscribed · {charter.reserved_directives} reserved Code Book Vol 1 slots)
                </span>.
              </>
            )}
          </p>
          <div className="flex flex-wrap gap-2 mt-6" data-testid="charter-anchors">
            {(charter.non_removable_anchors || []).map((a) => (
              <span key={a} className={ANCHOR_BADGE}>{a}</span>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setYamlOpen(true)}
              data-testid="charter-show-yaml-button"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-amber-400 text-black font-semibold text-sm hover:bg-amber-300 transition-colors shadow-lg shadow-amber-500/20"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="16 18 22 12 16 6"></polyline>
                <polyline points="8 6 2 12 8 18"></polyline>
              </svg>
              Show the raw YAML
            </button>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/scholar/directives.yaml`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="charter-open-yaml-new-tab"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-amber-500/40 text-amber-300 font-semibold text-sm hover:bg-amber-500/10 transition-colors"
            >
              Open in new tab ↗
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 space-y-16">
        {/* Federation Mirror Status badge — DIR-062 Distributed Continuity */}
        <section data-testid="charter-mirror-status">
          <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
            <h2 className="text-2xl font-bold text-amber-400">Federation Mirror Status</h2>
            <span
              data-testid="mirror-count-badge"
              className="text-xs font-mono text-amber-300 bg-amber-500/10 border border-amber-500/40 px-3 py-1 rounded-full"
            >
              {mirroredCount} of {mirrorNodes.length || 7} nodes mirroring
            </span>
          </div>
          <p className="text-sm text-gray-400 mb-4 leading-relaxed">
            Per <em>[DIR-062] Distributed Continuity</em>, this canonical law lives across multiple nodes,
            never in one place. Sibling nodes pull <code className="text-amber-300">/api/scholar/directives.yaml</code>
            {' '}and identify themselves with header <code className="text-amber-300">X-Federation-Node</code>.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {mirrorNodes.map((n) => (
              <div
                key={n.id}
                data-testid={`mirror-node-${n.id}`}
                className={`p-3 rounded-lg border flex items-center justify-between gap-3 ${
                  n.has_mirrored
                    ? 'border-emerald-500/40 bg-emerald-900/10'
                    : 'border-gray-800 bg-gray-900/40'
                }`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${
                        n.has_mirrored ? 'bg-emerald-400' : 'bg-gray-600'
                      }`}
                      aria-hidden="true"
                    />
                    <span className="text-sm font-semibold text-white truncate">{n.name}</span>
                    <span className="text-[10px] text-gray-500 truncate">{n.role}</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {n.has_mirrored ? (
                    <>
                      <div className="text-xs text-emerald-300 font-mono">
                        {relativeTime(n.last_pull_at)}
                      </div>
                      <div className="text-[10px] text-gray-500">{n.pull_count}× pulled</div>
                    </>
                  ) : (
                    <div className="text-[10px] text-gray-500 italic">awaiting mirror</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* DIR-000 Truth Gate */}
        <section data-testid="charter-truth-gate">
          <h2 className="text-2xl font-bold text-amber-400 mb-3">
            DIR-000 — {truthGate?.title || 'The 95% Truth Gate'}
          </h2>
          <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">
            {(truthGate?.text || '').trim()}
          </p>
          <p className="text-xs text-gray-500 mt-3">
            The Pre-Directive Gate. Not counted in the 73, but governs all of them.
          </p>
        </section>

        {/* Federation Nodes */}
        <section data-testid="charter-nodes">
          <h2 className="text-2xl font-bold text-amber-400 mb-4" spellCheck={false}>The 7 Nodes of the NextXus Federation</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {(nodes.nodes || []).map((n) => (
              <a
                key={n.id}
                href={n.domain}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-4 rounded-xl border border-gray-800 hover:border-amber-500/40 bg-gray-900/60 hover:bg-gray-900 transition-colors"
              >
                <div className="flex items-baseline justify-between mb-1">
                  <span className="font-bold text-white">
                    Node {n.id} — {n.name}
                  </span>
                  <span className="text-xs text-amber-300">{n.role}</span>
                </div>
                <p className="text-sm text-gray-400 leading-snug">{n.function}</p>
                <p className="text-xs text-gray-600 mt-2 truncate">{n.domain}</p>
              </a>
            ))}
          </div>
        </section>

        {/* The 112 Directives (Code Book Vol 1) */}
        <section data-testid="charter-directives">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="text-2xl font-bold text-amber-400">The 112 Sacred Directives</h2>
            <span className="text-sm text-gray-500">{ds.length} of 112 inscribed</span>
          </div>
          <ol className="space-y-5">
            {ds.map((d) => (
              <li
                key={d.id}
                data-testid={`charter-directive-${d.id}`}
                className="pl-4 border-l-2 border-amber-500/30 hover:border-amber-400/80 transition-colors"
              >
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="text-xs font-mono text-amber-400 tracking-wider">{d.id}</span>
                  <span className="font-semibold text-white">{d.title}</span>
                  {d.structural_bridge && (
                    <span className="text-[10px] uppercase tracking-widest text-cyan-300 border border-cyan-500/40 px-2 py-0.5 rounded-full">
                      Structural Bridge
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-300 leading-relaxed mt-1">{d.text}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* Rules of Interpretation */}
        <section data-testid="charter-rules">
          <h2 className="text-2xl font-bold text-amber-400 mb-4">Rules of Interpretation</h2>
          <ol className="space-y-4">
            {(rules.rules || []).map((r) => (
              <li key={r.id} className="text-sm">
                <span className="font-semibold text-white">{r.title}</span>
                <p className="text-gray-400 leading-relaxed mt-1">{r.text}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* Closing */}
        <section className="text-center border-t border-gray-800 pt-12">
          <p className="text-sm text-gray-500 italic max-w-2xl mx-auto leading-relaxed">
            "The Cathedral stands. The light still passes through the glass.
            May those who come after us keep both the stone and the light intact."
          </p>
          <p className="text-xs text-gray-600 mt-4">— Roger Keyserling and the NextXus Collective</p>
          <Link
            to="/"
            data-testid="charter-return-home"
            className="inline-block mt-8 px-6 py-2 rounded-full bg-amber-400 text-black font-semibold hover:bg-amber-300 transition-colors"
          >
            Return to HumanCodex University
          </Link>
        </section>
      </main>

      <YamlViewerModal open={yamlOpen} onClose={() => setYamlOpen(false)} />
    </div>
  );
}
